// PromptDiff — inline word-level redline between two persona prompts.
//
// Renders removed words struck-through (alert) and added words highlighted
// (sage) so the owner can see exactly how a version changed. LCS over
// whitespace-preserving tokens — more than fast enough for short persona text.

type Token = { text: string; kind: 'same' | 'add' | 'del' };

function tokenize(s: string): string[] {
  // Keep trailing whitespace on each token so re-joining preserves spacing.
  // `''.match(...)` returns null, so the `?? []` is load-bearing — it makes the
  // empty-before / empty-after edge cases (all-add / all-del) fall out correctly.
  return s.match(/\S+\s*|\s+/g) ?? [];
}

function diffTokens(a: string[], b: string[]): Token[] {
  const n = a.length;
  const m = b.length;
  // LCS length table (suffix DP).
  const dp: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i -= 1) {
    for (let j = m - 1; j >= 0; j -= 1) {
      dp[i]![j] = a[i] === b[j] ? dp[i + 1]![j + 1]! + 1 : Math.max(dp[i + 1]![j]!, dp[i]![j + 1]!);
    }
  }
  const out: Token[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      out.push({ text: b[j]!, kind: 'same' });
      i += 1;
      j += 1;
    } else if (dp[i + 1]![j]! >= dp[i]![j + 1]!) {
      out.push({ text: a[i]!, kind: 'del' });
      i += 1;
    } else {
      out.push({ text: b[j]!, kind: 'add' });
      j += 1;
    }
  }
  while (i < n) {
    out.push({ text: a[i]!, kind: 'del' });
    i += 1;
  }
  while (j < m) {
    out.push({ text: b[j]!, kind: 'add' });
    j += 1;
  }
  return out;
}

export default function PromptDiff({ before, after }: { before: string; after: string }) {
  const tokens = diffTokens(tokenize(before), tokenize(after));
  const unchanged = tokens.every((t) => t.kind === 'same');
  return (
    <div className="text-sm leading-relaxed whitespace-pre-wrap">
      {unchanged ? (
        <span className="text-muted-foreground">No textual changes.</span>
      ) : (
        tokens.map((t, idx) =>
          t.kind === 'same' ? (
            <span key={idx} className="text-foreground/80">
              {t.text}
            </span>
          ) : t.kind === 'add' ? (
            <span key={idx} className="rounded-sm bg-sage/15 text-sage">
              {t.text}
            </span>
          ) : (
            <span key={idx} className="rounded-sm bg-alert/10 text-alert line-through">
              {t.text}
            </span>
          ),
        )
      )}
    </div>
  );
}
